/*-----------------------------------------------------------------------------*
 *  TABS
 *-----------------------------------------------------------------------------*
 *
 *  Accessible tabs.
 *
 *  @author  Luca Rosaldi
 *  @version 1.0.0
 *
 */

const KEYCODE_ARROW_LEFT  = 37;
const KEYCODE_ARROW_RIGHT = 39;
const KEYCODE_ARROW_DOWN  = 40;

/**
 * @constructor
 *
 * @param {Node} element
 */
function Tabs( element, options ) {
  this.element = element;

  this.options = Object.assign({
    onInit:   function( currentTabContent ) {},
    onChange: function( currentTabContent, previousTabContent ) {}
  }, options );

  this.tabsNav = this.element.querySelector( 'ul' );
  this.tabs = this.tabsNav.querySelectorAll( 'a' );
  this.tabsContent = this.element.querySelectorAll( '[id]' );

  this.clickHandler = this.clickHandler.bind( this );
  this.keyPressHandler = this.keyPressHandler.bind( this );

  // keep a reference to the active tab
  this.activeTab = null;
  this.activeTabIndex = null;
  this.activeTabContent = null;

  this.init();
}

/**
 * Initialize module.
 */
Tabs.prototype.init = function() {
  const that = this;

  this.tabsNav.setAttribute( 'role', 'tablist' );

  let activeTab = this.getActiveTab() || this.tabs[0];

  if ( window.location.hash && this.element.querySelector( window.location.hash ) ) {
    activeTab = this.tabsNav.querySelector( '[href="' + window.location.hash + '"]' );
  }

  [].forEach.call( this.tabs, ( tab ) => {

    tab.parentNode.setAttribute( 'role', 'presentation' );
    tab.setAttribute( 'role', 'tab' );
    tab.setAttribute( 'id', 'tab-' + tab.getAttribute( 'href' ).split( '#' )[1] );

    const tabContent = that.getTabContent( tab );

    // disable tab if related section does not exist
    if ( ! tabContent ) {
      tab.setAttribute( 'disabled', 'disabled' );
      tab.setAttribute( 'tabindex', '-1' );
      tab.addEventListener( 'click', ( e ) => e.preventDefault() );
      return;
    }

    tab.addEventListener( 'click', that.clickHandler );
    tab.addEventListener( 'keydown', that.keyPressHandler );

    tabContent.setAttribute( 'role', 'tabpanel' );
    tabContent.setAttribute( 'tabindex', '-1' );
    tabContent.setAttribute( 'aria-labelledby', tab.id );

    this.deactivate( tab );
  });

  this.activate( activeTab, false );

  this.options.onInit( this.activeTabContent );
};

/**
 * Get index of a specific tab inside the tabs nav.
 *
 * @param  {Node} tab
 * @return {Int}
 */
Tabs.prototype.getTabIndex = function( tab ) {
  return [].indexOf.call( this.tabs, tab );
};

/**
 * Get section associated to specific tab.
 *
 * @param  {Node} tab
 * @return {Int}
 */
Tabs.prototype.getTabContent = function( tab ) {
  return this.tabsContent[ this.getTabIndex( tab ) ];
};

/**
 * Get the active tab.
 *
 * @return {Node}
 */
Tabs.prototype.getActiveTab = function() {
  return this.tabsNav.querySelector( '[aria-selected]' );
};

/**
 * Activate a tab.
 *
 * @param  {Node}       tab          Target tab
 * @param  {Node}       [updateHash] Update the hash in the url
 * @return {Node|false}              Activated tab on success, false otherwise
 */
Tabs.prototype.activate = function( tab, updateHash ) {
  const tabIndex = this.getTabIndex( tab );
  const tabContent = this.getTabContent( tab );

  if ( ! tabContent ) {
    return false;
  }

  this.activeTab = tab;
  this.activeTabIndex = tabIndex;
  this.activeTabContent = tabContent;

  this.activeTab.removeAttribute( 'tabindex' );
  this.activeTab.setAttribute( 'aria-selected', 'true' );
  this.activeTabContent.removeAttribute( 'aria-hidden' );

  if ( updateHash && window.history ) {
    window.history.pushState( null, null, this.activeTab.href );
  }

  this.element.setAttribute( 'data-tab-active', this.activeTabIndex + 1 );

  return this.activeTab;
};

/**
 * Deactivate a tab.
 *
 * @param  {Node}       tab Target tab
 * @return {Node|false}     Deactivated tab on success, false otherwise
 */
Tabs.prototype.deactivate = function( tab ) {
  const tabContent = this.getTabContent( tab );

  if ( ! tabContent ) {
    return false;
  }

  tab.setAttribute( 'tabindex', '-1' );
  tab.removeAttribute( 'aria-selected' );
  tabContent.setAttribute( 'aria-hidden', 'true' );

  return tab;
};

/**
 * Switch from one tab to another.
 *
 * @param  {Node} currentTab Tab which is currently active
 * @param  {Node} targetTab  Tab to activate
 * @param  {Node} updateHash Update the hash in the url
 * return  {Node}            The active tab after the change
 */
Tabs.prototype.switch = function( currentTab, targetTab, updateHash ) {
  if ( this.activate( targetTab ) ) {

    this.deactivate( currentTab );
    this.activate( targetTab, updateHash );

    this.options.onChange( this.activeTabContent, this.getTabContent( currentTab ) );
    return targetTab;
  }

  return currentTab;
};

/**
 * Go back to previous tab.
 */
Tabs.prototype.prev = function() {
  const prevTab = this.tabs[ this.activeTabIndex - 1 ] || this.tabs[ this.tabs.length -1 ];
  return this.switch( this.activeTab, prevTab );
};

/**
 * Advance to next tab.
 */
Tabs.prototype.next = function() {
  const nextTab = this.tabs[ this.activeTabIndex + 1 ] || this.tabs[0];
  return this.switch( this.activeTab, nextTab );
};

/**
 * Handle click events.
 *
 * @param {Event} event
 */
Tabs.prototype.clickHandler = function( event ) {
  const currentTab = this.tabsNav.querySelector( '[aria-selected]' );
  const targetTab = event.currentTarget;

  if ( currentTab !== targetTab ) {
    this.switch( currentTab, targetTab );
    targetTab.focus();
  }

  event.preventDefault();
  event.stopPropagation();
};

/**
 * Handle keyboard events.
 *
 * @param {Event} event
 */
Tabs.prototype.keyPressHandler = function( event ) {
  const expectedKeyCodes = [ KEYCODE_ARROW_LEFT, KEYCODE_ARROW_RIGHT, KEYCODE_ARROW_DOWN ];
  const keyCode = event.which;

  // react only to expected key codes
  if ( expectedKeyCodes.indexOf( keyCode ) === -1 ) {
    return;
  }
  if ( KEYCODE_ARROW_DOWN === keyCode ) {
    this.activeTabContent.focus();
  }
  else if ( KEYCODE_ARROW_LEFT === keyCode ) {
    this.prev();
  }
  else if ( KEYCODE_ARROW_RIGHT === keyCode ) {
    this.next();
  }

  event.preventDefault();
};

export default Tabs;
